/**
 * The Aged debt tab's table (docs/features/v1-completion/L_credit_control_spec.md §3.4, §4.6; mock-up
 * L_credit_control.html state 1): one row per payer that owes on CIS credit-account invoices, with what it owes in
 * the five ageing buckets, the total, how much of that is disputed, its limit and its oldest overdue invoice, and
 * a totals row for the whole filtered set (not the page). Disputed is part of the buckets, not a sixth bucket.
 *
 * Presentational: the page holds the filters, the paging and which payer is open; the open payer's invoices
 * (`panel`) show under its row.
 */
import { Fragment, type ReactNode } from "react";
import type {
  AgedDebtPayer,
  AgedDebtTotals,
} from "@shared-core/data/aged-debt-types";
import {
  AGED_DEBT_BUCKETS,
  oldestOverdueLabel,
} from "@shared-core/domain/credit-control-presentation";
import { formatQuotedPrice } from "@shared-core/domain/formatters";
import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../primitives/table";

const money = (pence: number): string =>
  pence === 0 ? "–" : formatQuotedPrice(pence);

export function AgedDebtPayerTable({
  payers,
  totals,
  totalPayers,
  isFiltered,
  openPayerRef,
  onTogglePayer,
  panel,
}: {
  payers: AgedDebtPayer[];
  /** The filtered set summed, not just this page. */
  totals: AgedDebtTotals;
  totalPayers: number;
  isFiltered: boolean;
  /** The payer whose open invoices are showing, if any. */
  openPayerRef: string | null;
  onTogglePayer: (payer: AgedDebtPayer) => void;
  /** The open payer's invoices, shown in a row under it. */
  panel: ReactNode;
}) {
  return (
    <Table className="[&_td:first-child]:pl-6 [&_td:last-child]:pr-6 [&_th:first-child]:pl-6 [&_th:last-child]:pr-6">
      <TableHeader>
        <TableRow>
          <TableHead>Payer</TableHead>
          {AGED_DEBT_BUCKETS.map((bucket) => (
            <TableHead key={bucket.label} className="text-right">
              {bucket.label}
            </TableHead>
          ))}
          <TableHead className="text-right">Total owed</TableHead>
          <TableHead className="text-right">Disputed</TableHead>
          <TableHead className="text-right">Limit</TableHead>
          <TableHead>Oldest</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {payers.map((payer) => {
          const isOpen = payer.billing_account_ref === openPayerRef;
          return (
            <Fragment key={payer.billing_account_ref}>
              <TableRow>
                <TableCell className="align-top">
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto p-0 font-medium"
                    aria-expanded={isOpen}
                    onClick={() => onTogglePayer(payer)}
                  >
                    {payer.payer_name}
                  </Button>{" "}
                  {payer.on_stop && (
                    <Badge variant="error" className="font-normal">
                      On stop
                    </Badge>
                  )}
                  <div className="text-xs font-normal text-muted-foreground">
                    {payer.customer_code}
                  </div>
                </TableCell>
                {AGED_DEBT_BUCKETS.map((bucket) => (
                  <TableCell
                    key={bucket.label}
                    className="text-right tabular-nums"
                  >
                    {money(payer[bucket.column])}
                  </TableCell>
                ))}
                <TableCell className="text-right font-medium tabular-nums">
                  {formatQuotedPrice(payer.outstanding_pence)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {payer.disputed_pence > 0 ? (
                    <Badge variant="warning" className="font-normal">
                      {formatQuotedPrice(payer.disputed_pence)}
                    </Badge>
                  ) : (
                    "–"
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {payer.credit_limit_pence === null
                    ? "–"
                    : formatQuotedPrice(payer.credit_limit_pence)}
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {oldestOverdueLabel(payer.oldest_overdue_days)}
                </TableCell>
              </TableRow>
              {isOpen && (
                <TableRow className="bg-muted/30 hover:bg-muted/30">
                  <TableCell colSpan={10} className="px-6 py-3">
                    {panel}
                  </TableCell>
                </TableRow>
              )}
            </Fragment>
          );
        })}
        <TableRow className="border-t-2 font-semibold hover:bg-transparent">
          <TableCell>
            {isFiltered
              ? `${totalPayers} ${totalPayers === 1 ? "payer matches" : "payers match"}`
              : `All ${totalPayers} ${totalPayers === 1 ? "payer" : "payers"}`}
          </TableCell>
          {AGED_DEBT_BUCKETS.map((bucket) => (
            <TableCell key={bucket.label} className="text-right tabular-nums">
              {money(totals[bucket.column])}
            </TableCell>
          ))}
          <TableCell className="text-right tabular-nums">
            {formatQuotedPrice(totals.outstanding_pence)}
          </TableCell>
          <TableCell className="text-right tabular-nums">
            {money(totals.disputed_pence)}
          </TableCell>
          <TableCell />
          <TableCell />
        </TableRow>
      </TableBody>
    </Table>
  );
}
